import get from 'lodash.get';

import { getButtonCellAssetClassNames } from './get-class-names';
import { ImageHover } from '~/components/image-hover';
import { useMemo } from 'react';
import { mapBreakpointToClassNames, mapResponsiveStylesToArray } from './utils';
import clsx from 'clsx';

export type AssetImageProps = { styles: any; entry: any; selected: boolean };

type States = 'hover' | 'selected' | 'selected_hover';

const getSwitch = (styles: any, key: States) => {
  return get(styles, `asset_${key}_switch`, false);
};

const getImageSrc = (styles: any, key: States) => {
  return get(styles, `asset_${key}`, undefined);
};

function getPayloadImgSrc(
  imgKey: string | undefined,
  payload: any,
  staticAsset?: string
): string | undefined {
  try {
    const mediaItem = payload?.media_group?.[0]?.media_item?.find(
      (mediaItem: any) => {
        return mediaItem.key === imgKey;
      }
    );

    /**
     * Resolve image source in the following order:
     * 1. Check if static asset is provided and use it.
     * 2. If not, check if media item with the specified key exists in the payload and use its source.
     * 3. If neither is available, return undefined.
     */
    return staticAsset || mediaItem?.src || undefined;
  } catch (error) {
    return undefined;
  }
}

export const ImageComponent: React.FC<{
  enabled: boolean;
  entry: any;
  selected: boolean;
  className: string;
  defaultImage: string | undefined;
  assetImageKey?: string;
  hoverImageSrc?: string;
  hoverImageSwitch?: boolean;
  hoverImageKey?: string;
  selectedImageSrc?: string;
  selectedImageSwitch?: boolean;
  selectedImageKey?: string;
  selectedHoverImageSrc?: string;
  selectedHoverAssetSwitch?: boolean;
  selectedHoverImageKey?: string;
}> = ({
  enabled,
  entry,
  selected,
  className,
  defaultImage,
  assetImageKey,
  hoverImageSwitch,
  selectedImageSwitch,
  selectedHoverAssetSwitch,
  selectedHoverImageKey,
  hoverImageKey,
  selectedImageKey,
  ...props
}) => {
  const defaultImageSrc = useMemo(
    () => getPayloadImgSrc(assetImageKey, entry, defaultImage),
    [entry, defaultImage, assetImageKey]
  );

  const hoverImageSrc = useMemo(
    () => getPayloadImgSrc(hoverImageKey, entry, props.hoverImageSrc),
    [entry, hoverImageKey, props.hoverImageSrc]
  );

  const selectedImageSrc = useMemo(
    () => getPayloadImgSrc(selectedImageKey, entry, props.selectedImageSrc),
    [entry, selectedImageKey, props.selectedImageSrc]
  );

  const selectedHoverImageSrc = useMemo(
    () =>
      getPayloadImgSrc(
        selectedHoverImageKey,
        entry,
        props.selectedHoverImageSrc
      ),
    [entry, selectedHoverImageKey, props.selectedHoverImageSrc]
  );

  return enabled ? (
    <ImageHover
      defaultImageSwitch
      defaultImageSrc={defaultImageSrc}
      hoverImageSwitch={hoverImageSwitch}
      hoverImageSrc={hoverImageSrc}
      selectedImageSwitch={selectedImageSwitch}
      selectedImageSrc={selectedImageSrc}
      selectedHoverAssetSwitch={selectedHoverAssetSwitch}
      selectedHoverImageSrc={selectedHoverImageSrc}
      classNames={className}
      isSelected={selected}
    />
  ) : null;
};

export const AssetImage = ({
  styles: stylesProp,
  entry,
  selected,
}: AssetImageProps) => {
  const className = getButtonCellAssetClassNames();

  const components = useMemo(
    () =>
      mapResponsiveStylesToArray<any, React.FC<any>>(
        stylesProp,
        (styles, key) => {
          const wrapperClassName = clsx(
            'hidden', // hidden by default, shown when matching breakpoint
            mapBreakpointToClassNames(key) + 'flex'
          );
          const assetSwitchEnabled = get(styles, 'asset_switch', false);
          const assetImageKey = get(styles, 'asset_image_key', '');
          const assetDefault = get(styles, 'asset_default', '');

          const hoverImageSwitch = getSwitch(styles, `hover`);
          const hoverImageKey = get(styles, `asset_hover_image_key`, '');
          const hoverImageSrc = getImageSrc(styles, `hover`);

          const selectedImageSwitch = getSwitch(styles, `selected`);
          const selectedImageKey = get(styles, `asset_selected_image_key`, '');
          const selectedImageSrc = getImageSrc(styles, `selected`);

          const selectedHoverAssetSwitch = getSwitch(styles, `selected_hover`);
          const selectedHoverImageKey = get(
            styles,
            `asset_selected_hover_image_key`,
            ''
          );
          const selectedHoverImageSrc = getImageSrc(styles, `selected_hover`);

          // eslint-disable-next-line react/display-name
          return ({
            entry,
            selected,
            className,
          }: {
            entry: any;
            selected: boolean;
            className: string;
          }) => (
            <span className={wrapperClassName}>
              <ImageComponent
                entry={entry}
                selected={selected}
                className={className}
                defaultImage={assetDefault}
                assetImageKey={assetImageKey}
                enabled={assetSwitchEnabled}
                hoverImageSwitch={hoverImageSwitch}
                hoverImageKey={hoverImageKey}
                hoverImageSrc={hoverImageSrc}
                selectedImageSwitch={selectedImageSwitch}
                selectedImageKey={selectedImageKey}
                selectedImageSrc={selectedImageSrc}
                selectedHoverAssetSwitch={selectedHoverAssetSwitch}
                selectedHoverImageKey={selectedHoverImageKey}
                selectedHoverImageSrc={selectedHoverImageSrc}
              />
            </span>
          );
        }
      ),
    [stylesProp]
  );

  return (
    <span className="flex-shrink-0">
      {components.map((Component, index) => (
        <Component
          key={index}
          entry={entry}
          selected={selected}
          className={className}
        />
      ))}
    </span>
  );
};
